import { useCallback, useMemo, useState } from "react";
import { FormControl, Grid, Popover } from "@mui/material";
import { IFullPairingPosition, IUser } from "@vmosolutions/oc-interfaces";
import { DropTargetMonitor, useDrop } from "react-dnd";
import { baseRowHeight } from "src/pages/CrewSchedule/gl/CrewScheduleGL";
import { ICrewDraggable } from "../PairingEditorWindow/CrewMember";
import { PositionBadge } from "../PositionBadge";
import { OpenFlyingIcons } from "../OpenFlyingIcons";
import classNames from "classnames";
import { SelectField } from "src/components/MUI5FormikComponents/SelectField";
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { updatePairingEditor } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { assignCrewToPositions } from "./crewPositionHelpers";

interface ICrewPairingPositionProps {
  windowId: string;
  pairingPosition: IFullPairingPosition;
  index: number;
  openFlyingDisplayIndex?: number; // Used to determine if position is selected in pairing editor.
}

interface ICrewOptions {
  value: IUser;
  label: string;
}

export const CrewPairingPosition = (props: ICrewPairingPositionProps): JSX.Element => {
  const { pairingPosition, index, openFlyingDisplayIndex, windowId } = props;
  const dispatch = useDispatch();
  const crew = useSelector((state: IRootState) => state.crewSchedule.crewMembers);
  const storedPairing = useSelector((state: IRootState) => state.crewSchedule.pairingEditors[windowId]);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? `persistent-popover-${index}` : undefined;

  /**
   * Assign a crew member to this position slot, first clearing them out of any other position they
   * already hold on the pairing. Shared by the drop target and the available crew dropdown.
   */
  const assignCrewToPosition = useCallback(
    (crewMember: IUser): void => {
      if (!storedPairing || !crewMember) {
        return;
      }
      const positions: IFullPairingPosition[] = assignCrewToPositions(
        storedPairing.positions,
        index,
        pairingPosition.position,
        crewMember
      );
      dispatch(updatePairingEditor(windowId, { positions }));
    },
    [storedPairing, index, pairingPosition.position, windowId, dispatch]
  );

  const [, drop] = useDrop({
    accept: ["CREW_REASSIGN"], //"CREW_ASSIGN",
    drop(i: any, monitor: DropTargetMonitor) {
      const item: ICrewDraggable = i;
      assignCrewToPosition(item.crewMember);
    },
    //hover(item: ICrewDraggable, monitor: DropTargetMonitor) {
    // Debug.log(item)
    //},
    canDrop(i: any, monitor: DropTargetMonitor) {
      const item: ICrewDraggable = i;
      return !!item.crewMember.crewInfo.positions.find(
        (position: any) => position.title === pairingPosition.position.title
      );
    },
    collect(monitor: DropTargetMonitor) {
      return {
        isOver: monitor.isOver(),
      };
    },
  });

  const isPosSelected = useMemo(() => {
    return openFlyingDisplayIndex > -1 && openFlyingDisplayIndex === index;
  }, [index, openFlyingDisplayIndex]);
  // DISPLAY WHEN POSITION SLOTS ARE EMPTY

  const handleOpen = (event: any): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  // Picking a crew member assigns them straight away, there is no separate confirmation step.
  const handleDropdownChange = (option: ICrewOptions | null): void => {
    if (!option?.value) {
      return;
    }
    assignCrewToPosition(option.value);
    handleClose();
  };

  const crewOptions = useMemo(() => {
    return crew
      .filter(
        (c) =>
          c.crewInfo.isActive &&
          c.crewInfo.positions.some((position) => position.title === pairingPosition.position.title)
      )
      .map((cp) => {
        return {
          value: cp,
          label: `${cp.preferredName ? cp.preferredName : cp.firstName} ${cp.lastName}${
            cp.employeeId ? ` [${cp.employeeId}]` : ""
          }`,
        };
      });
  }, [crew, pairingPosition.position.title]);

  const formatCrewOptionLabel = (option: ICrewOptions): JSX.Element => {
    const user: any = option?.value;
    if (!user) {
      return <>{option?.label}</>;
    }

    const { firstName, lastName, preferredName, employeeId, crewInfo } = user;
    const highPos =
      crewInfo?.positions && crewInfo.positions.length > 0
        ? [...crewInfo.positions].sort((a: any, b: any) => a.displayOrder - b.displayOrder)[0]
        : null;

    const displayName = `${preferredName ? preferredName : firstName} ${lastName ?? ""}`.trim();

    return (
      <Grid container className="crew_option_container" alignItems="center" wrap="nowrap">
        {highPos && <PositionBadge position={highPos} filled={true} isInactive={!crewInfo?.isActive} />}
        <Grid item style={{ marginLeft: 10 }}>
          {`${displayName}${employeeId ? ` [${employeeId}]` : ""}`}
        </Grid>
      </Grid>
    );
  };

  const customFilterOption = (option: any, inputValue: string): boolean => {
    const { email, lastName, firstName, preferredName, employeeId } = option.data.value;
    return (
      (email && email.toLowerCase().includes(inputValue.toLowerCase())) ||
      (lastName && lastName.toLowerCase().includes(inputValue.toLowerCase())) ||
      (firstName && firstName.toLowerCase().includes(inputValue.toLowerCase())) ||
      (preferredName && preferredName.toLowerCase().includes(inputValue.toLowerCase())) ||
      (employeeId && employeeId.toString().toLowerCase().includes(inputValue.toLowerCase()))
    );
  };

  return (
    <>
      <Grid
        className={classNames("crew_details", `position_block_${index}`, isPosSelected && "selected")}
        style={{ display: "flex", height: baseRowHeight }}
        key={pairingPosition?.position?.title}
        ref={drop}
        item
        id={`position_block_${index}`}
      >
        <Grid container alignItems={"flex-start"} wrap="nowrap">
          <Grid container direction="column" style={{ width: "auto" }}>
            <Grid item style={{ padding: 4 }}>
              <PositionBadge position={pairingPosition?.position} />
            </Grid>
            <Grid item style={{ width: 14, height: 20, margin: "0px 8px" }}>
              <OpenFlyingIcons flyingStatusType={pairingPosition?.openFlyingStatus} />
            </Grid>
          </Grid>
          <Grid item className="crew_member empty" style={{ height: baseRowHeight }} onClick={handleOpen}>
            <span className="drop_crew">Drop {pairingPosition?.position?.description}</span>
          </Grid>
        </Grid>
      </Grid>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "left",
        }}
      >
        <div
          style={{
            padding: "10px",
            minWidth: "300px",
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <FormControl fullWidth>
            <SelectField
              name="addCrew"
              options={crewOptions}
              value={null}
              onChange={handleDropdownChange}
              label="Available Crew"
              formatOptionLabel={formatCrewOptionLabel}
              filterOption={customFilterOption}
            />
          </FormControl>
        </div>
      </Popover>
    </>
  );
};
